import React from 'react';
import clsx from 'clsx';

import { formatDateByFormat, DATE_FORMATS } from '../../../../../../../../utils/dateUtils';
import { KEYBOARD_EVENTS } from '../../../../../../../../utils/constants/commonConstants';
import { classifyAddable } from '../../../../../../../../common/Reports/addFileToReports';
import LoadingSpinner from '../../../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';

import FileActionsMenu from './FileActionsMenu';
import { FILE_TYPE_ICONS, DEFAULT_FILE_ICON } from './FileAttachmentCard.constants';
import { FileAttachmentCardProps } from './FileAttachmentCard.types';

import styles from './FileAttachmentCard.module.scss';

const FileAttachmentCard: React.FC<FileAttachmentCardProps> = ({
    fileUrl,
    fileName,
    fileType,
    createdAt,
    onDownload,
    className,
    truncateName = true,
    onCardClick,
    onAddToReports,
    loading = false,
}) => {
    const ext = (fileType || fileName.split('.').pop() || '').toLowerCase();
    const icon = FILE_TYPE_ICONS[ext] || DEFAULT_FILE_ICON;
    const formattedTimestamp = formatDateByFormat(createdAt, DATE_FORMATS.LIBRARY_FILE_TIMESTAMP);
    const canAddToReports = Boolean(onAddToReports) && classifyAddable(fileName, fileType) !== null;
    // While opening, the card must not re-trigger the open (a second click would
    // no-op in the hook, but disabling the affordance is clearer).
    const clickable = onCardClick && !loading;

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!clickable) return;
        // Space (' ') has no KEYBOARD_EVENTS constant; it is kept literal so the
        // role="button" card activates on both Enter and Space per a11y convention.
        if (event.key === KEYBOARD_EVENTS.ENTER || event.key === ' ') {
            event.preventDefault();
            onCardClick!();
        }
    };

    return (
        <div
            className={clsx(styles.FileAttachmentCard, !truncateName && styles.FullName, clickable && styles.Clickable, className)}
            aria-busy={loading}
            {...(clickable && { role: 'button', tabIndex: 0, onClick: onCardClick, onKeyDown: handleKeyDown })}
        >
            {loading ? (
                <span className={styles.FileIcon}>
                    <LoadingSpinner height="1.25rem" color="var(--primary-purple)" />
                </span>
            ) : (
                <img className={styles.FileIcon} src={icon} alt={ext || 'file'} />
            )}
            <div className={styles.FileInfo}>
                <div className={styles.FileName} title={fileName}>
                    {fileName}
                </div>
                {formattedTimestamp && <div className={styles.FileTimestamp}>{formattedTimestamp}</div>}
            </div>
            <div className={styles.FileMenuWrapper} onClick={(event) => event.stopPropagation()}>
                <FileActionsMenu
                    items={[
                        ...(clickable ? [{ name: 'Open', onClick: onCardClick! }] : []),
                        ...(canAddToReports ? [{ name: 'Add to Workspace', onClick: onAddToReports! }] : []),
                        {
                            name: 'Download',
                            onClick: () => onDownload(fileUrl, fileName),
                        },
                    ]}
                />
            </div>
        </div>
    );
};

export default FileAttachmentCard;
